import { useCallback, useEffect, useState } from 'react';
import { errorMessage } from '../api/client';

export type AsyncState<T> = {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => void;
  /**
   * Remplace la donnée sans rappeler le serveur.
   *
   * Sert au canal en direct : un événement poussé porte tout ce qu'il faut
   * pour l'insérer dans une frise déjà chargée, et redemander la frise
   * entière pour une ligne serait payer une page pour un mot. À ne pas
   * employer pour une valeur que seul le serveur sait calculer.
   */
  patch: (update: (current: T) => T) => void;
};

/**
 * Chargement d'une ressource distante. Une erreur est conservée et affichée :
 * aucun appel ne retombe silencieusement sur une valeur par défaut.
 */
export function useAsync<T>(
  loader: (signal: AbortSignal) => Promise<T>,
  deps: readonly unknown[],
): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  const reload = useCallback(() => setTick((value) => value + 1), []);

  const patch = useCallback((update: (current: T) => T) => {
    setData((current) => (current === null ? current : update(current)));
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    setLoading(true);
    setError(null);

    loader(controller.signal)
      .then((result) => {
        if (!active) return;
        setData(result);
      })
      .catch((cause: unknown) => {
        if (!active || controller.signal.aborted) return;
        setError(errorMessage(cause));
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);

  return { data, error, loading, reload, patch };
}
